import * as echarts from "echarts"
import { merge } from "lodash-es"
import { ComponentPropsWithRef, useEffect, useMemo, useRef } from "react"
import { cn } from "styles/cn"
import { asDate, formatDate } from "utils/date"

type DataValue = string | number | Date | null | undefined

interface LineChartProps extends ComponentPropsWithRef<"div"> {
	data: DataValue[][]
}

export const LineChart = ({
	className,
	data = [],
	...props
}: LineChartProps) => {
	const chartRef = useRef(null)
	const chartOptions = useMemo(() => {
		if (data.length === 0) {
			return {}
		}
		const firstDate = asDate(data[0][0] as string)
		return {
			xAxis: {
				min: firstDate.startOf("month").toISOString(),
				max: firstDate.endOf("month").toISOString(),
			},
			series: buildSeries(data),
		}
	}, [data])

	useEffect(() => {
		if (!chartRef.current) {
			return
		}

		const observer = new ResizeObserver(() => {
			if (!chartRef.current) {
				return
			}
			const chart = echarts.getInstanceByDom(chartRef.current)
			if (chart) {
				chart.resize()
			}
		})

		observer.observe(chartRef.current)

		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		let chartInstance = chartRef.current
			? echarts.getInstanceByDom(chartRef.current)
			: null
		if (!chartInstance) {
			echarts
				.init(chartRef.current, null, { renderer: "svg" })
				.setOption(merge({}, BASE_OPTIONS, chartOptions))
		} else {
			chartInstance.setOption(merge({}, BASE_OPTIONS, chartOptions))
		}

		return () => {
			chartInstance?.dispose()
		}
	}, [chartOptions])

	return (
		<div {...props} ref={chartRef} className={cn("size-full", className)} />
	)
}

const BASE_OPTIONS: echarts.EChartsOption = {
	tooltip: {
		trigger: "axis",
		formatter: function (params: any) {
			const [day, value] = params[0].value
			return `${value} lbs · ${formatDate(day)}`
		},
		axisPointer: {
			type: "line",
			lineStyle: {
				color: "#b2b4e6",
				width: 2,
				type: "solid",
			},
		},
	},
	xAxis: {
		type: "time",
		axisTick: {
			show: false,
		},
		axisLabel: {
			align: "left",
			hideOverlap: true,
			showMinLabel: true,
			showMaxLabel: true,
			alignMaxLabel: "right",
			formatter: function (value) {
				const tick = asDate(value)
				const tickDate = tick.date()
				if ([1, tick.endOf("month").date()].includes(tickDate)) {
					// tick labels for min and max
					return "{MMM} {d}"
				}
				// ticks in the middle
				return "{d}"
			},
		},
	},
	yAxis: {
		type: "value",
		axisLabel: {
			formatter: "{value} lbs",
		},
		position: "right",
		splitLine: {
			lineStyle: {
				type: "dashed",
			},
		},
	},
	grid: {
		left: 10,
		right: 10,
		bottom: 10,
		top: 10,
		containLabel: true,
	},
}

const buildSeries = (data: DataValue[][]) => {
	return [
		{
			...LINE_SERIES_OPTIONS,
			...(data.length > 1
				? {
						itemStyle: {
							color: "rgba(0,0,0,0)",
							borderColor: "rgba(0,0,0,0)",
						},
					}
				: {}),
			data,
		},
	]
}

const LINE_SERIES_OPTIONS = {
	type: "line",
	lineStyle: {
		color: "#8183d8",
	},
	areaStyle: {
		color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
			{
				offset: 0,
				color: "#5456cc",
			},
			{
				offset: 1,
				color: "#fff",
			},
		]),
	},
	emphasis: {
		itemStyle: {
			color: "#8183d8",
			borderColor: "#ffffff",
			borderWidth: 2,
		},
	},
	symbol: "circle",
	symbolSize: 8,
}
